import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const RtReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, metadata, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const rtMeasurements = measurements.filter((measurement) => measurement.reportType === 'RT');
  const pipeLengthMm = pipeLengthM * 1000;
  const effectiveFrequency = metadata?.frequency || frequency || 'per_pipe';
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['No project limits', 'Configure project limits to show live values', true],
  ]);
  const iqiSensitivityLimit = getProjectLimitMax(projectLimitFields, 'rt_iqi_sensitivity_pct', 2);
  const porosityLengthLimit = getProjectLimitMax(projectLimitFields, 'rt_porosity_length_mm', 4);
  const slagLengthLimit = getProjectLimitMax(projectLimitFields, 'rt_slag_length_mm', 6);
  const getRtIndicationStatus = (flawType: string, lengthMm: number, sensitivity: number, disposition: string) => {
    if (flawType === 'crack' || flawType === 'lack_of_fusion' || disposition !== 'accept') return 'FAIL';
    if (sensitivity > iqiSensitivityLimit) return 'FAIL';
    if (flawType === 'slag') return lengthMm <= slagLengthLimit ? 'PASS' : 'FAIL';
    return lengthMm <= porosityLengthLimit ? 'PASS' : 'FAIL';
  };
  const indications = [
    { id: 'r1', posMm: 3120, flawType: 'porosity', lengthMm: 2.4, areaMm2: 1.1, disposition: 'accept', status: 'PASS' },
    { id: 'r2', posMm: 8200, flawType: 'slag', lengthMm: 3.6, areaMm2: 1.8, disposition: 'accept', status: 'PASS' },
  ].filter((indication) => !isSampleDataDeleted(deletedMeasurementIds, `rt-${indication.id}`)).map((indication) => ({
    ...indication,
    measurement: createSampleMeasurement('RT', `rt-${indication.id}`, 'RT indication', indication.lengthMm.toFixed(1), 'mm', 'IQI <= 2%; porosity <= 4 mm; slag <= 6 mm', indication.status, {
      fieldPath: 'rt_indication',
      rtIndicationId: indication.id,
      rtPositionMm: String(indication.posMm),
      rtFlawType: indication.flawType,
      rtLengthMm: indication.lengthMm.toFixed(1),
      rtAreaMm2: indication.areaMm2.toFixed(1),
      rtDisposition: indication.disposition,
      rtIqiSensitivityPct: '1.8',
      rtExposureClass: 'B',
    }),
  })).concat([
    ...rtMeasurements.map((measurement) => {
      const flawType = measurement.rtFlawType || 'porosity';
      const lengthMm = Number(measurement.rtLengthMm) || 0;
      const sensitivity = Number(measurement.rtIqiSensitivityPct) || 0;
      const disposition = measurement.rtDisposition || 'accept';
      return {
        id: measurement.rtIndicationId || measurement.id,
        posMm: Number(measurement.rtPositionMm) || 0,
        flawType,
        lengthMm,
        areaMm2: Number(measurement.rtAreaMm2) || 0,
        disposition,
        status: getRtIndicationStatus(flawType, lengthMm, sensitivity, disposition),
        measurement,
      };
    }),
  ]);
  const exposureClass = rtMeasurements[0]?.rtExposureClass || 'B';
  const iqiSensitivity = Number(rtMeasurements[0]?.rtIqiSensitivityPct || 1.8);
  const filmStrips = indications.map((indication, index) => ({
    id: indication.id,
    label: indication.measurement?.rtIndicationId || indication.id,
    stripLengthM: Math.max(0.5, Math.min(4.5, indication.lengthMm || 1)),
    index,
  }));
  const failCount = indications.filter((indication) => indication.status === 'FAIL').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Indications along weld seam</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{metadata?.source ? formatLabel(metadata.source) : 'RT'} · class {exposureClass}</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('RT')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Indication
          </Button>
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-4">
            <div className="relative h-24 overflow-hidden rounded-full border border-[#D9DEE6] bg-gradient-to-b from-white via-[#EEF0F3] to-[#E2E5EA] shadow-inner">
              {[0, 0.25, 0.5, 0.75, 1].map((point) => (
                <div key={point} className="absolute bottom-0 top-0 w-px bg-[#D9DEE6]/70" style={{ left: `${point * 100}%` }} />
              ))}
              <span className="absolute bottom-3 left-5 font-mono text-[10px] text-heritage-grey">End 1</span>
              <span className="absolute bottom-3 right-5 font-mono text-[10px] text-heritage-grey">End 2</span>
              <span className="absolute right-6 top-2 font-mono text-[10px] text-heritage-grey">seam · 12:00</span>
              {indications.map((indication) => (
                <button
                  key={indication.id}
                  type="button"
                  title={`${indication.id} ${indication.flawType} ${indication.lengthMm} mm`}
                  onClick={() => indication.measurement && onEditMeasurement?.(indication.measurement)}
                  className={cn(
                    'absolute h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm',
                    indication.status === 'FAIL' ? 'bg-readable-red' : indication.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green',
                    indication.measurement ? 'cursor-pointer' : 'cursor-default'
                  )}
                  style={{
                    left: `calc(${Math.max(1, Math.min(99, pipeLengthMm > 0 ? (indication.posMm / pipeLengthMm) * 100 : 0))}% - 5px)`,
                    top: '40%',
                  }}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <span className="font-mono text-[10px] text-heritage-grey">0 mm</span>
              {indications.map((indication) => (
                <span key={indication.id} className="flex items-center gap-2 font-mono text-[11px] text-heritage-grey">
                  <MiniStatusDot status={indication.status} />
                  {indication.id} · {indication.flawType} · {indication.lengthMm.toFixed(1)} mm
                </span>
              ))}
              <span className="ml-auto font-mono text-[10px] text-heritage-grey">{pipeLengthM.toFixed(2)} m</span>
            </div>
          </CardContent>
        </Card>

        <div>
          <SectionHead title={`Film strip - ${filmStrips.length} exposures`} subtitle="Film strips are generated from saved RT indications" />
          <Card className="border-[#E2E5EA]">
            <CardContent className="p-3">
              {filmStrips.length === 0 ? (
                <div className="p-3 text-[13px] text-heritage-grey">No RT indications saved yet.</div>
              ) : (
                <div className="flex gap-3 overflow-x-auto">
                  {filmStrips.map((strip) => (
                    <div key={strip.id} className="min-w-[210px] rounded-lg border border-[#E2E5EA] bg-white p-2">
                      <div className="relative h-[66px] overflow-hidden rounded bg-gradient-to-r from-[#1B1F27] via-[#444A55] to-[#1B1F27]">
                        {[24, 58, 96, 132, 174].map((x) => (
                          <span key={x} className="absolute top-4 h-7 w-px bg-white/20" style={{ left: x }} />
                        ))}
                        <span className="absolute top-[28px] h-2 rounded-sm bg-white/40" style={{ left: `${50 + strip.index * 28}px`, width: `${Math.max(12, strip.stripLengthM * 5)}px` }} />
                      </div>
                      <div className="mt-2 flex justify-between gap-2 text-[10px] text-heritage-grey">
                        <span className="font-mono text-text-grey">{strip.label}</span>
                        <span>{strip.stripLengthM.toFixed(1)} m strip</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Sensitivity & exposure" />
            <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-5">
              {[
                ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
                ['Accept. std', metadata?.note || '-'],
                ['Exposure class', exposureClass],
                ['IQI', 'Wire ISO 19232 W10'],
                ['IQI sensitivity', `${iqiSensitivity.toFixed(1)} % (req <= ${iqiSensitivityLimit.toFixed(1)} %)`],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className="mt-1 font-mono text-[12px] font-semibold text-blueprint-blue">{value}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Indication detail" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3 text-right">Pos (mm)</th>
                  <th className="px-4 py-3">Flaw type</th>
                  <th className="px-4 py-3 text-right">Length mm</th>
                  <th className="px-4 py-3 text-right">Area mm2</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {indications.map((indication) => (
                  <tr key={indication.id} onClick={() => indication.measurement && onEditMeasurement?.(indication.measurement)} className={cn(indication.measurement && 'cursor-pointer hover:bg-page-bg')}>
                    <td className="px-4 py-3 font-mono font-semibold">{indication.id}</td>
                    <td className="px-4 py-3 text-right font-mono">{indication.posMm.toLocaleString()}</td>
                    <td className="px-4 py-3">{indication.flawType}</td>
                    <td className="px-4 py-3 text-right font-mono">{indication.lengthMm.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right font-mono">{indication.areaMm2.toFixed(1)}</td>
                    <td className="px-4 py-3">{indication.disposition}</td>
                    <td className="px-4 py-3"><StatusBadge status={indication.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {indication.measurement && (
                        <ItemActionButtons measurement={indication.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Indications', `${rtMeasurements.length} saved`, true], ['Rejects', failCount, true]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
